import { useEffect, useRef, useCallback } from 'react';
import throttle from 'lodash/throttle';
import theme from 'lib/theme';

const POLYGON_COLOR = theme.colors.brand.primary;
const POLYGON_SMOOTHNESS = 30;

const POLYGON_OPTIONS = {
  clickable: false,
  fillColor: POLYGON_COLOR,
  fillOpacity: 0.1,
  geodesic: true,
  strokeColor: POLYGON_COLOR,
  strokeWeight: 3,
};

const POLYLINE_OPTIONS = {
  clickable: false,
  strokeColor: POLYGON_COLOR,
  strokeWeight: 3,
};

export const useMapPolygon = (enabled, { gMap, gMaps, gMapDom }, onPolygonUpdated) => {
  const polygon = useRef(); // Non-react event callbacks can't access state, so have to store in a ref

  const finishPolyline = (polyline) => {
    const path = polyline.getPath();
    polyline.setMap(null);

    return new gMaps.Polygon({
      ...POLYGON_OPTIONS,
      map: gMap,
      path,
    });
  };

  const finishPolygon = (polygon) => {
    const latLngBounds = new gMaps.LatLngBounds();
    polygon.getPath().forEach((element) => {
      latLngBounds.extend(element);
    });
    const { south: swLat, west: swLng, north: neLat, east: neLng } = latLngBounds.toJSON();

    const isWithinPolygon =
      () =>
      ({ latitude: lat, longitude: lng }) => {
        const point = new gMaps.LatLng({ lat, lng });
        return gMaps.geometry.poly.containsLocation(point, polygon);
      };

    onPolygonUpdated({
      query: { swLat, swLng, neLat, neLng },
      isWithinPolygon,
    });
  };

  const drawFreehand = () => {
    if (polygon.current) {
      polygon.current.setMap(null);
    }

    const polyline = new gMaps.Polyline({
      ...POLYLINE_OPTIONS,
      map: gMap,
    });

    gMaps.event.clearListeners(gMap, 'mousemove');
    gMaps.event.clearListeners(gMap, 'mouseup');
    gMaps.event.addListener(
      gMap,
      'mousemove',
      throttle(({ latLng }) => {
        polyline.getPath().push(latLng);
      }, POLYGON_SMOOTHNESS),
    );

    gMaps.event.addListenerOnce(gMap, 'mouseup', () => {
      polygon.current = finishPolyline(polyline);
      finishPolygon(polygon.current);

      gMaps.event.clearListeners(gMap, 'mousemove');
    });
  };

  const clearPolygon = useCallback(() => {
    if (polygon.current) {
      polygon.current.setMap(null);
    }
  }, [polygon]);

  // Need to re-add the listener when maps changes because drawFreehand() cannot access state objects as a non-react listener
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (gMaps && gMapDom) {
      gMaps.event.clearListeners(gMapDom, 'mousedown');
      gMaps.event.clearListeners(gMapDom, 'touchstart');
      if (enabled) {
        gMaps.event.addDomListener(gMapDom, 'mousedown', drawFreehand);
        gMaps.event.addDomListener(gMapDom, 'touchstart', drawFreehand);
      }
    }
  }, [enabled, gMaps, gMapDom]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return { clearPolygon };
};
